<template>
  <section
    class="search"
    aria-labelledby="search-title">
    <div class="search__inner">
      <label
        id="search-title"
        class="search__label"
        for="header-search-input">
        {{ t("header.searchPlaceholder") }}
      </label>

      <div class="search__field">
        <input
          id="header-search-input"
          ref="searchInput"
          v-model="searchTerm"
          type="text"
          class="search__input"
          data-testid="header-search-input"
          :placeholder="t('header.searchPlaceholder')"
          aria-autocomplete="list"
          :aria-expanded="shouldShowDropdown"
          :aria-labelledby="'search-title'"
          @input="handleInput"
          @keydown.down.prevent="moveFocus(1)"
          @keydown.up.prevent="moveFocus(-1)"
          @keydown.enter.prevent="handleEnter"
          @keydown.esc="resetSearch" />

        <button
          type="button"
          class="search__cta"
          @click="triggerSearch">
          {{ t("header.searchButton") }}
        </button>
      </div>

      <div
        v-if="shouldShowDropdown"
        class="search__dropdown"
        role="listbox"
        data-testid="search-dropdown">
        <div
          v-if="loading"
          class="search__state">
          {{ t("general.loading") }}
        </div>
        <div
          v-else-if="error"
          class="search__state">
          {{ t("search.error") }}
        </div>
        <template v-else>
          <template v-if="hasResults">
            <ul class="search__list">
              <li
                v-for="item in flatResultsWithIndex"
                :key="item.key"
                :class="['search__item', { 'search__item--active': item.index === focusedIndex }]"
                role="option"
                data-testid="search-item"
                :data-source="item.sourceId"
                @mouseenter="setFocus(item.index)"
                @mousedown.prevent="selectResult(item)">
                <div class="search__item-text">
                  <div class="search__item-category">
                    {{ item.categoryLabel }}
                  </div>
                  <div class="search__item-title">
                    {{ item.displayTitle }}
                  </div>
                  <!-- <div v-if="item.subtitle" class="search__item-subtitle">{{ item.subtitle }}</div> -->
                </div>
                <veneta-icon
                  class="search__item-icon"
                  :width="26"
                  :height="26"
                  color="red"
                  :is-hovered="item.index === focusedIndex"
                  event-listener />
              </li>
            </ul>
          </template>
          <div
            v-else
            class="search__state">
            {{ t("search.noResults") }}
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { storeToRefs } from 'pinia'
  import {
    computed, nextTick, ref
  } from 'vue'

  import {
    useI18n, useLocalePath, useRoute
  } from '#imports'
  import VenetaIcon from '~/components/VenetaIcon.vue'
  import { useSearchStore } from '~/store/search'
  import { getStoreSearchDisplayTitle } from '~/utils/stores'

  const { t } = useI18n()
  const localePath = useLocalePath()

  const searchStore = useSearchStore()
  const {
    query,
    loading,
    pristine,
    flatResults,
    hasResults,
    error
  } = storeToRefs(searchStore)

  const searchInput = ref(null)
  const searchTriggered = ref(false)
  const props = defineProps({
    isOpen: {
      type: Boolean,
      default: false
    }
  })
  const emit = defineEmits([ 'navigate' ])
  const focusedIndex = ref(-1)

  const MIN_TERM_LENGTH = 2

  const searchTerm = computed({
    get: () => query.value || '',
    set: value => {
      query.value = value
    }
  })

  const categoryLabels = computed(() => ({
    kitchens: t('header.kitchens'),
    livings: t('header.living'),
    stores: t('header.dealers'),
    complements: t('navigation.breadcrumb.complements'),
    lifeStories: t('header.lifeStories'),
    awards: t('header.awards'),
    initiatives: t('header.dealerInitiatives'),
    magazine: t('header.magazine'),
    news: t('header.news'),
    maintenance: t('header.maintenanceTips')
  }))

  const flatResultsWithIndex = computed(() => {
    let indexCounter = 0

    return flatResults.value.map(item => ({
      ...item,
      index: indexCounter++,
      categoryLabel: categoryLabels.value[item.sourceId] || item.sourceId,
      displayTitle: item?.sourceId === 'stores' ? getStoreSearchDisplayTitle(item) : (item?.title || '')
    }))
  })

  const shouldShowDropdown = computed(() => {
    return (
      searchTriggered.value
      && (loading.value || !pristine.value || hasResults.value)
    )
  })

  watch(
    () => props.isOpen,
    isOpen => {
      if (isOpen) {
        focusInput()
      } else {
        resetSearch()
      }
    }
  )

  const route = useRoute()

  watch(
    () => route.fullPath,
    () => {
      resetSearch()
    }
  )

  const focusInput = () => {
    nextTick(() => {
      searchInput.value?.focus?.()
    })
  }

  const resetSearch = () => {
    searchStore.reset(true)
    focusedIndex.value = -1
    searchTriggered.value = false
  }

  const handleInput = () => {
    focusedIndex.value = -1
    searchTriggered.value = false

    const term = searchTerm.value.trim()
    if (term.length < MIN_TERM_LENGTH) {
      searchStore.reset(false)
      return
    }
  }

  const triggerSearch = () => {
    const term = searchTerm.value.trim()
    if (term.length < MIN_TERM_LENGTH) {
      searchTriggered.value = false
      searchStore.reset(false)
      return
    }

    focusedIndex.value = -1
    searchTriggered.value = true
    searchStore.execute(true)
  }

  const handleEnter = () => {
    if (focusedIndex.value >= 0) {
      selectFocused()
      return
    }

    triggerSearch()
  }

  const moveFocus = direction => {
    if (!hasResults.value) return

    const total = flatResultsWithIndex.value.length
    const next = (focusedIndex.value + direction + total) % total
    focusedIndex.value = next
  }

  const setFocus = index => {
    focusedIndex.value = index
  }

  const selectFocused = () => {
    if (focusedIndex.value < 0) return
    const item = flatResultsWithIndex.value[focusedIndex.value]
    selectResult(item)
  }

  const selectResult = item => {
    if (!item) return
    if (item.routeName) {
      // routeQuery: usata dai negozi senza scheda pubblicata, che puntano allo
      // store locator con ?store=<id> per evidenziare la card giusta.
      const target = localePath({
        name: item.routeName,
        params: item.routeParams,
        ...(item.routeQuery ? { query: item.routeQuery } : {})
      })
      emit('navigate', target)
      searchStore.reset(true)
    }
  }
</script>

<style scoped lang="scss">
.search {
  width: 100%;
  min-height: 100vh;
  border-radius: 0 0 20px 20px;
  padding: var(--space-xxl) var(--space-m) var(--space-xxl);

  @include from-desktop {
    background: var(--color-white);
    border-radius: 0;
    padding: calc(196px - var(--header-height)) var(--space-m) var(--space-xxl);
  }

  @include up-to-desktop {
    padding: var(--space-xl) var(--space-s) var(--space-xl);
    background-color: #f3f3f3;
  }
}

.search__inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);

  @include from-desktop {
    max-width: 896px;
    gap: var(--space-xl);
  }
}

.search__label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.search__field {
  position: relative;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-s);
  border-bottom: 1px solid var(--color-black);
  padding-bottom: var(--space-xs);
  margin-bottom: var(--space-xl);

  @include from-desktop {
    gap: var(--space-m);
    padding-bottom: var(--space-xxs);
    margin-bottom: var(--space-xl);
  }
}

.search__input {
  width: 100%;
  border: none;
  background: transparent;
  font-size: clamp(20px, 2vw, 24px);
  font-weight: 400;
  color: var(--color-black);
  outline: none;
  padding: 0;

  &::placeholder {
    color: var(--color-dark-grey);
    opacity: 0.7;
  }

  @include from-desktop {
    font-size: clamp(28px, 3vw, 40px);
    line-height: 1.3;
    padding: 0;
    background: transparent;
  }
}

.search__cta {
  @include body-s;

  color: var(--color-red, #dc0814);
  background: transparent;
  border: none;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0 0 2px;
  flex-shrink: 0;
  letter-spacing: 0.02em;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  font-size: clamp(18px, 3.4vw, 20px);
  font-family: var(--font-family-poppins, inherit);

  &:hover {
    text-decoration: underline;
  }
}

.search__dropdown {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-l);
  padding-bottom: 40px;

  @include from-desktop {
    background: var(--color-white);
    border-radius: 12px;
    padding: 72px 0 0;
    gap: var(--space-m);
  }
}

.search__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.search__item {
  padding: var(--space-m) 0;
  border-radius: 0;
  cursor: pointer;
  transition: color 120ms ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-m);
  border-bottom: 1px solid var(--color-grey, #cbcccd);

  &:hover,
  &.search__item--active {
    background: transparent;
  }

  @include from-desktop {
    padding: var(--space-n) 0;
    border-radius: 0;
    border-bottom: 1px solid var(--color-grey, #cbcccd);
    background: transparent;

    &:hover,
    &.search__item--active {
      background: transparent;
    }
  }
}

.search__item-text {
  display: grid;
  gap: var(--space-xxs);
}

.search__item-category {
  @include body-xs;

  text-transform: uppercase;
  margin-bottom: 6px;
  letter-spacing: 0.08em;
  color: var(--color-dark-grey);
}

.search__item-title {
  @include body-m;

  color: var(--color-dark-grey);
  line-height: 1.6;

  @include from-desktop {
    font-size: 18px;
    line-height: 1.6;
  }
}

.search__item-subtitle {
  @include body-xs;

  color: var(--color-dark-grey);
}

.search__item-icon {
  flex-shrink: 0;
  align-self: center;
}

.search__state {
  @include body-s;

  color: var(--color-dark-grey);
  padding: var(--space-s) 0;

  @include from-desktop {
    padding: var(--space-m) 0;
  }
}
</style>
